import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { laborOfferRead, laborOfferRespond } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

/** Public worker-side landing when they tap the SMS/email link.
 *  Shows the job offer summary + big ACCEPT / DECLINE buttons. */
export default function LaborResponsePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);
  const [contractorPhone, setContractorPhone] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await laborOfferRead(token);
      setData(r);
      setDecision(r.response_decision);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "This offer link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const respond = async (choice) => {
    setBusy(true); setError(null);
    try {
      const r = await laborOfferRespond(token, choice);
      setDecision(r.decision);
      setContractorPhone(r.contractor_phone);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save your response.");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <div style={{ padding: 60, textAlign: "center", color: "#5b5e69" }} data-testid="labor-loading">Loading offer…</div>;
  if (error && !data) return (
    <div style={pageWrap} data-testid="labor-error">
      <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center" }}>
        <h1 style={h1}>OFFER UNAVAILABLE</h1>
        <p style={{ color: "#5b5e69" }}>{error}</p>
        <Link to="/available-today/me" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none", marginTop: 20 }}>← MY WORKER PROFILE</Link>
      </div>
    </div>
  );
  if (!data) return null;
  const r = data.request;

  return (
    <div style={pageWrap} data-testid="labor-response-page">
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · AVAILABLE TODAY · JOB OFFER</div>
        <h1 style={h1}>{r.contractor_business_name}</h1>
        <div style={{ color: "#5b5e69", fontSize: 15, marginBottom: 18 }}>
          <strong>{(r.skills || []).join(", ")}</strong> · {r.city}{r.state ? `, ${r.state}` : ""} · ${r.hourly_offered_usd}/hr · {(r.urgency || "").replace("_", " ")}
        </div>

        <div style={card}>
          <SummaryRow label="Workers needed" value={`${r.workers_accepted}/${r.workers_needed} filled so far`} />
          <SummaryRow label="Start" value={r.start_at ? new Date(r.start_at).toLocaleString() : "As soon as you can"} />
          {r.end_at && <SummaryRow label="End" value={new Date(r.end_at).toLocaleString()} />}
          <SummaryRow label="Match confidence" value={`${data.score}%`} />
          {r.required_osha && <SummaryRow label="Requires" value="OSHA certified" />}
          {r.description && (
            <div style={{ marginTop: 12, padding: 12, background: "#F5F7FA", borderRadius: 10 }}>
              <div style={{ color: "#5b5e69", fontSize: 11, letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>JOB DETAILS</div>
              <div style={{ color: NAVY, fontSize: 14, whiteSpace: "pre-wrap" }} data-testid="labor-description">{r.description}</div>
            </div>
          )}
        </div>

        {r.status !== "open" && !decision && (
          <div style={infoBox} data-testid="labor-closed">
            This request is <strong>{r.status.toUpperCase()}</strong> — the contractor has already filled the crew or cancelled.
          </div>
        )}

        {decision === "accept" && (
          <div style={{ ...card, background: "#E8F5EE", border: `2px solid ${GREEN}` }} data-testid="labor-accepted">
            <div style={{ color: GREEN, fontWeight: 700, letterSpacing: 2, fontSize: 14, marginBottom: 8 }}>✓ YOU&apos;RE CONFIRMED</div>
            <p style={{ color: NAVY, fontSize: 15, marginBottom: 8 }}>
              Reach out to {r.contractor_business_name} to lock in the details.
            </p>
            {r.contractor_phone && (
              <a href={`tel:${r.contractor_phone}`}
                 data-testid="labor-contractor-call-link"
                 style={{ display: "inline-block", background: NAVY, color: GOLD, padding: "14px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, textDecoration: "none", marginTop: 6 }}>
                📞 CALL {r.contractor_phone}
              </a>
            )}
          </div>
        )}

        {decision === "decline" && (
          <div style={{ ...card, background: "#FDECEC", border: `2px solid ${RED}` }} data-testid="labor-declined">
            <div style={{ color: RED, fontWeight: 700, letterSpacing: 2, fontSize: 14, marginBottom: 8 }}>DECLINED</div>
            <p style={{ color: NAVY, fontSize: 14 }}>
              No worries — we&apos;ll keep matching you to future jobs.
            </p>
          </div>
        )}

        {!decision && r.status === "open" && (
          <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
            <button onClick={() => respond("accept")} disabled={busy}
                    data-testid="labor-accept-button"
                    style={{ ...btnPrimary, flex: 1, background: GREEN, color: "#fff", opacity: busy ? 0.6 : 1 }}>
              ✓ ACCEPT JOB
            </button>
            <button onClick={() => respond("decline")} disabled={busy}
                    data-testid="labor-decline-button"
                    style={{ ...btnPrimary, flex: 1, background: "#fff", color: RED, border: `2px solid ${RED}`, opacity: busy ? 0.6 : 1 }}>
              DECLINE
            </button>
          </div>
        )}

        {error && <div data-testid="labor-inline-error" style={errBox}>{error}</div>}

        <p style={{ marginTop: 26, textAlign: "center" }}>
          <Link to="/available-today/me" data-testid="labor-my-profile-link" style={{ color: NAVY, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
            ← MY WORKER PROFILE
          </Link>
        </p>
      </div>
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f0f2f5" }}>
    <span style={{ color: "#5b5e69", fontSize: 12, letterSpacing: 1.5, fontWeight: 700 }}>{label.toUpperCase()}</span>
    <span style={{ color: NAVY, fontSize: 14, fontWeight: 600, textAlign: "right" }}>{value}</span>
  </div>
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const card = { background: "#fff", padding: "20px 24px", borderRadius: 16, border: "1px solid #e5e7ee", marginBottom: 16 };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 44, color: NAVY, margin: "6px 0 10px" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "18px 24px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const errBox = { color: RED, background: "#FDECEC", padding: "12px 14px", borderRadius: 10, marginTop: 14, fontSize: 14 };
const infoBox = { color: "#5b5e69", background: "#F5F7FA", padding: "14px 16px", borderRadius: 10, marginTop: 14, fontSize: 14 };
